import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {environment} from '../../../environments/environment';
import {
  DataSetMetric,
  DataSetMetricsGroup,
  DataSourceMetric,
  MetricCalculatedType,
} from '../interfaces/metrics';
import {Serializer, SerializerResponse, Serializers} from '../interfaces/serializer';
import {map, tap} from 'rxjs/operators';
import {SelectOptions} from '../interfaces/form';
import {SerializerUtils} from '../libraries/serializer-utils';
import {AppService} from './app.service';
import {Site} from '../interfaces/site';

@Injectable()
export class MetricsService {

  public readonly operators: SelectOptions<string, string> = [
    { key: 'equals', text: 'conditions.equals' },
    { key: 'not_equals', text: 'conditions.not_equals' },
    { key: 'lower_than', text: 'conditions.lower_than' },
    { key: 'lower_than_equals', text: 'conditions.lower_than_equals' },
    { key: 'greater_than', text: 'conditions.greater_than' },
    { key: 'greater_than_equals', text: 'conditions.greater_than_equals' }
  ];

  public calculatedMetricFormatTypes: SelectOptions<MetricCalculatedType, string> = [
    { key: 'numeric', text: 'settings.numeric' },
    { key: 'percentage', text: 'settings.percentage' },
    { key: 'currency', text: 'settings.currency' }
  ];

  constructor(
    private readonly _http: HttpClient,
    private readonly _appS: AppService
  ) {}

  public getDataSourceMetrics(dataset: Serializer<Site>): Observable<SerializerResponse<Serializers<DataSourceMetric>>> {
    return this._http.get<SerializerResponse<Serializers<DataSourceMetric>>>(`${environment.baseUrl}/api/${dataset.id}/data-source-metrics`)
      .pipe(
        tap((response: SerializerResponse<Serializers<DataSetMetric>>): void => {
          SerializerUtils.joinRelationships(response, ['data_source']);
        })
      );
  }

  public getDataSetMetrics(dataset: Serializer<Site>): Observable<SerializerResponse<Serializers<DataSetMetric>>> {
    return this._http.get<SerializerResponse<Serializers<DataSetMetric>>>(`${environment.baseUrl}/api/${dataset.id}/data-set-metrics`)
      .pipe(
        tap((response: SerializerResponse<Serializers<DataSetMetric>>): void => {
          SerializerUtils.joinRelationships(response, ['data_source', 'data_set_metrics_group', 'data_source_metric']);
        })
      );
  }

  public createDataSetMetric(dataset: Serializer<Site>, metric: Partial<DataSetMetric>): Observable<SerializerResponse<Serializer<DataSetMetric>>> {
    return this._http.post<SerializerResponse<Serializer<DataSetMetric>>>(`${environment.baseUrl}/api/${dataset.id}/data-set-metrics`, metric)
      .pipe(
        tap((response: SerializerResponse<Serializer<DataSetMetric>>): void => {
          SerializerUtils.joinRelationships(response, ['data_source', 'data_set_metrics_group', 'data_source_metric']);
        })
      );
  }

  public updateDataSetMetric(dataset: Serializer<Site>, metric: Serializer<DataSetMetric>, update: Partial<DataSetMetric>): Observable<SerializerResponse<Serializer<DataSetMetric>>> {
    return this._http.patch<SerializerResponse<Serializer<DataSetMetric>>>(`${environment.baseUrl}/api/${dataset.id}/data-set-metrics/${metric.id}`, update)
      .pipe(
        tap((response: SerializerResponse<Serializer<DataSetMetric>>): void => {
          SerializerUtils.joinRelationships(response, ['data_source', 'data_set_metrics_group', 'data_source_metric']);
        })
      );
  }

  public deleteDataSetMetric(dataset: Serializer<Site>, metric: Serializer<DataSetMetric>): Observable<SerializerResponse<Serializer<DataSetMetric>>> {
    return this._http.delete<SerializerResponse<Serializer<DataSetMetric>>>(`${environment.baseUrl}/api/${dataset.id}/data-set-metrics/${metric.id}`);
  }

  public getDataSetMetricsGroups(dataset: Serializer<Site>): Observable<SerializerResponse<Serializers<DataSetMetricsGroup>>> {
    return this._http.get<SerializerResponse<Serializers<DataSetMetricsGroup>>>(`${environment.baseUrl}/api/${dataset.id}/data-set-metrics-groups`)
      .pipe(
        map((response: SerializerResponse<Serializers<DataSetMetricsGroup>>) => {
          response.data = response.data.filter((group: Serializer<DataSetMetricsGroup>): boolean => group.attributes.slug !== 'cycle');
          return response;
        })
      )
  }

  public createDataSetMetricsGroup(dataset: Serializer<Site>, group: Partial<DataSetMetricsGroup>): Observable<SerializerResponse<Serializer<DataSetMetricsGroup>>> {
    return this._http.post<SerializerResponse<Serializer<DataSetMetricsGroup>>>(`${environment.baseUrl}/api/${dataset.id}/data-set-metrics-groups`, group);
  }

  public updateDataSetMetricsGroup(dataset: Serializer<Site>, group: Serializer<DataSetMetricsGroup>, update: Partial<DataSetMetricsGroup>): Observable<SerializerResponse<Serializer<DataSetMetricsGroup>>> {
    return this._http.patch<SerializerResponse<Serializer<DataSetMetricsGroup>>>(`${environment.baseUrl}/api/${dataset.id}/data-set-metrics-groups/${group.id}`, update);
  }

  public deleteDataSetMetricsGroup(dataset: Serializer<Site>, group: Serializer<DataSetMetricsGroup>): Observable<SerializerResponse<Serializer<DataSetMetricsGroup>>> {
    return this._http.delete<SerializerResponse<Serializer<DataSetMetricsGroup>>>(`${environment.baseUrl}/api/${dataset.id}/data-set-metrics-groups/${group.id}`);
  }

  public checkCalculatedMetricOperation(operation: string): Observable<boolean> {
    return this._http.post(`${environment.baseUrl}/api/${this._appS.datasetID}/data-set-metrics/check-operation`, {
      operation
    }).pipe(
      map((result: any) => {
        return result.valid;
      })
    );
  }
}
